import * as React from "react";
import { observer } from "mobx-react";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { Box, Typography, MenuItem, ListItemIcon, Button } from "@mui/material";
import { GroupWorkIcon } from "../../../icons";
import { CrudRow, CrudRows, FooterData, GridColumn } from "../types";
import { ColumnWidthInfo } from "../services/crudServices";
import { IMenuAction } from "../../../common/controls";
import { useFileCellAffordances } from "./fileCellAffordances";
import { useRootStore } from "../../../store/contexts";
import { graphqlToDateTime } from "../../../common/utils";
import { diaDeCalendario, formatearDia } from "../../../common/calendarDate";

type ShowItemsCompactProps = {
  label: string;
  // Descripción opcional, mostrada como subtítulo (mismo rol que en ItemsCRUDCompact).
  // Sirve para que código manual del proyecto que migró de ItemsCRUDCompact a ShowItemsCompact
  // (e.g. nested sets readOnly) pueda mantener la prop description sin tocar el JSX.
  description?: string;
  // CrudRows = Record<string, ...>, CrudRow[] = array. Aceptamos ambos porque
  // los stores pueden devolver entityValues.showXxx como array (cuando viene de la
  // query) o como record (cuando viene del CRUD). Object.values normaliza ambos.
  rows: CrudRows | CrudRow[];
  columns: GridColumn[];
  setColumnWidths?: (x: ColumnWidthInfo[]) => void;
  showLabel?: boolean;
  menuActions?: IMenuAction[];
  footerData?: FooterData;
  hidden?: boolean;
  // Habilita el ícono de descarga en celdas de tipo File.
  // entityName = nombre de la entidad de las filas (e.g. "EditionDocumentation").
  // La URL final es /download/<entityName>.<columnFileField>/<row.id>/<filename>.
  enableDownload?: boolean;
  entityName?: string;
  // Snapshot download mode: when both are set, file cells download through the
  // snapshot-aware endpoint (/<snapshotEntityName>.data/<snapshotRecordId>/<uuid>)
  // instead of the live-entity URL. The file's uuid comes from <col>_data.uuid;
  // row.id (the nested item id) is NOT used.
  snapshotEntityName?: string;
  snapshotRecordId?: string;
  // Agrupa las filas por el valor de una columna. Mismo contrato que ItemsCRUDCompact:
  // groupBy es el nombre de la columna por la que agrupar; groupColumnLabel es el label
  // mostrado en el header agrupador. Si groupBy no está en columns, se agrega como columna
  // adicional (oculta en celdas normales, visible solo en filas agrupadas).
  groupBy?: string;
  groupColumnLabel?: string;
  // Cuando está definido, las filas son clickeables y al hacer click se invoca
  // con `row.id`. El consumidor decide qué hacer (ej. abrir un modal con el
  // detalle completo de la fila en el contexto del snapshot view).
  onRowClick?: (rowId: string) => void;
};

export const ShowItemsCompact: React.FC<ShowItemsCompactProps> = observer(
  ({
    label,
    description,
    rows,
    columns,
    setColumnWidths,
    showLabel = true,
    menuActions = [],
    footerData,
    hidden = false,
    enableDownload = false,
    entityName,
    snapshotEntityName,
    snapshotRecordId,
    groupBy,
    groupColumnLabel,
    onRowClick,
  }) => {
    if (hidden) return null;
    const rootStore = useRootStore();
    const { langStore } = rootStore;
    const gridRows = React.useMemo(() => {
      return Object.values(rows);
    }, [rows]);

    // Estado controlado para grouping (mismo patrón que ItemsCRUDCompact).
    const [currentGrouping, setCurrentGrouping] = React.useState<string[]>(
      groupBy ? [groupBy] : []
    );
    const isGroupingRemoved = !!groupBy && currentGrouping.length === 0;

    const { renderFileCell, excelViewerElement } = useFileCellAffordances({
      entityName,
      snapshotEntityName,
      snapshotRecordId,
      enableDownload,
    });

    const mrtColumns = React.useMemo(() => {
      const baseColumns = columns.map((col) => {
        const baseColumn = {
          accessorKey: col.name,
          header: col.title || col.name,
          size: (col as any).width || 150,
          grow: true,
        };

        const fileDataKey = `${col.name}_data`;
        const footerCell = footerData?.[col.name];

        return {
          ...baseColumn,
          Footer: footerCell
            ? () => (
                <Box sx={{ fontWeight: 500, ...footerCell.sx }}>
                  {footerCell.value}
                </Box>
              )
            : undefined,
          Cell: ({ row, cell }: any) => {
            const fileData = row.original[fileDataKey];
            if (fileData?.mimetype) {
              return renderFileCell(
                row.original,
                col.name,
                fileData.mimetype,
                cell.getValue()
              );
            }
            const cellValue = cell.getValue();
            // Un date (día de calendario, "AAAA-MM-DD"), por el tipo declarado de la
            // columna: el formateador del idioma, sin la zona del navegador.
            if (col.fieldType === "Date") {
              return formatearDia(
                diaDeCalendario(cellValue),
                langStore.timeFormat
              );
            }
            // Los Date del values-mapper (un dateTime) no son renderizables como
            // children de React: van con su hora.
            if (cellValue instanceof Date) {
              return graphqlToDateTime(cellValue.toISOString());
            }
            // Resolve embedded multilang display fragments to the active language
            // (no-op for plain strings); leave non-strings untouched.
            return typeof cellValue === "string"
              ? langStore.displayIdByLang(cellValue)
              : cellValue;
          },
        };
      }) as MRT_ColumnDef<any>[];

      // Si groupBy está especificado y no es una columna visible, agregarla como
      // accessor para agrupar. groupedColumnMode="remove" la quita del render.
      if (groupBy && !columns.find((c) => c.name === groupBy)) {
        baseColumns.unshift({
          accessorKey: groupBy,
          header: groupColumnLabel || groupBy,
          size: 200,
          grow: true,
        } as MRT_ColumnDef<any>);
      }

      return baseColumns;
    }, [
      columns,
      footerData,
      renderFileCell,
      groupBy,
      groupColumnLabel,
      langStore,
    ]);

    const handleColumnSizeChange = React.useCallback(
      (columnSizingInfo: any) => {
        if (setColumnWidths) {
          const widthsArray = Object.entries(columnSizingInfo).map(
            ([columnName, width]) => ({
              columnName,
              width: width as number,
            })
          );
          setColumnWidths(widthsArray);
        }
      },
      [setColumnWidths]
    );

    return (
      <Box>
        {showLabel && (
          <Box sx={{ marginLeft: "10px", marginBottom: "8px" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
              {label}:
            </Typography>
            {description && (
              <Typography
                sx={{ fontSize: 12, fontWeight: 400, color: "text.secondary" }}
              >
                {description}
              </Typography>
            )}
          </Box>
        )}

        <MaterialReactTable
          layoutMode="grid"
          muiTableContainerProps={{ sx: { overflowX: "auto" } }}
          columns={mrtColumns}
          data={gridRows}
          enableEditing={false}
          enableRowActions={menuActions.length > 0}
          positionActionsColumn="first"
          enableColumnActions={true}
          enableColumnFilters={false}
          enablePagination={false}
          enableSorting={true}
          enableHiding={true}
          enableColumnOrdering={true}
          enableBottomToolbar={false}
          enableTopToolbar={true}
          enableGlobalFilter={false}
          enableDensityToggle={false}
          enableColumnResizing={!!setColumnWidths}
          columnResizeMode="onChange"
          onColumnSizingChange={handleColumnSizeChange}
          enableGrouping={true}
          groupedColumnMode="remove"
          enableExpandAll={false}
          state={{
            grouping: currentGrouping,
          }}
          onGroupingChange={setCurrentGrouping}
          initialState={{
            density: "compact",
            ...(groupBy ? { expanded: true } : {}),
          }}
          muiTableBodyRowProps={
            onRowClick
              ? ({ row }: any) => ({
                  sx: { cursor: row.getIsGrouped() ? "default" : "pointer" },
                  onClick: () => {
                    if (row.getIsGrouped()) return;
                    onRowClick(row.original.id);
                  },
                })
              : undefined
          }
          muiTableHeadCellProps={{
            sx: {
              verticalAlign: "middle",
            },
          }}
          muiTableBodyCellProps={({ row, column }: any) => ({
            sx: {
              ...(row.getIsGrouped() && {
                fontWeight: 600,
                backgroundColor: "action.hover",
                // La etiqueta de grupo vive en la celda expand (groupedColumnMode="remove");
                // al estrechar la columna a 40px debe desbordar sobre las celdas vacías de la fila.
                ...(column.id === "mrt-row-expand" && {
                  overflow: "visible",
                  whiteSpace: "nowrap",
                }),
              }),
            },
          })}
          localization={
            groupBy
              ? ({ groupedBy: langStore.groupedByLabel } as any)
              : undefined
          }
          renderTopToolbarCustomActions={
            groupBy
              ? () => (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {isGroupingRemoved && (
                      <Button
                        variant="outlined"
                        startIcon={<GroupWorkIcon />}
                        onClick={() => setCurrentGrouping([groupBy!])}
                        size="small"
                      >
                        {langStore.restoreGroupingCommand}
                      </Button>
                    )}
                  </Box>
                )
              : undefined
          }
          displayColumnDefOptions={{
            "mrt-row-expand": {
              header: "",
              size: 40,
              minSize: 40,
              maxSize: 40,
              grow: false,
            },
            ...(menuActions.length > 0
              ? {
                  "mrt-row-actions": {
                    header: "",
                    size: 80,
                    minSize: 80,
                    maxSize: 80,
                    grow: false,
                  },
                }
              : {}),
          }}
          renderRowActionMenuItems={
            menuActions.length > 0
              ? ({ row, closeMenu }) => {
                  const rowData = row.original;

                  return menuActions.map((action, index) => {
                    const isEnabled = action.isActionEnabled
                      ? action.isActionEnabled(rowData)
                      : true;

                    return (
                      <MenuItem
                        key={`action-${index}`}
                        disabled={!isEnabled}
                        onClick={(e) => {
                          action.onClick(rowData, e);
                          closeMenu();
                        }}
                      >
                        <ListItemIcon>{action.icon}</ListItemIcon>
                        {action.label}
                      </MenuItem>
                    );
                  });
                }
              : undefined
          }
        />
        {excelViewerElement}
      </Box>
    );
  }
);
